import {
  SessionItem,
  SessionItemData,
  SessionItemKind,
} from "@/mds/components/sessions/session-item";
import {
  ClientStatusSessionPartState,
  SessionEventKind,
  SessionStatus,
} from "@/mds/components/sessions/session.types";
import { MDSSessionError, MDSSessionErrorKind } from "./slices/ui/mds-session-context.ui.types";
import { SessionPartKind } from "@/mds/components/sessions/session.types";

export const SESSION_STATUS_ITEM_ID = "session-status-item";
export const SESSION_STATUS_EVENT_ID = "session-status-event";
export const SESSION_STATUS_PART_ID = "session-status-part";

/**
 * This function does a few things:
 * - Filters out any buffering parts
 * - If the session is in a "processing" state, it adds a "processing" status item
 * - If the session is in an "error" state, it adds an "error" status item
 */
export const finalizeSessionItems = ({
  sessionItems,
  sessionStatus,
  sessionError,
}: {
  sessionItems: SessionItem[];
  sessionStatus: SessionStatus;
  sessionError: MDSSessionError | null;
}): SessionItem[] => {
  const sessionStatusItemData: SessionItemData = (() => {
    if (sessionStatus === SessionStatus.Processing) {
      return {
        part: {
          id: SESSION_STATUS_PART_ID,
          kind: SessionPartKind._ClientStatus,
          state: ClientStatusSessionPartState.LoadingIndicator,
          data: {},
        },
        context: {
          eventKind: SessionEventKind.AgentActionV1,
          eventId: SESSION_STATUS_EVENT_ID,
          partId: SESSION_STATUS_PART_ID,
          itemId: SESSION_STATUS_ITEM_ID,
          lastArtifactId: null,
        },
      };
    }

    if (sessionError) {
      return {
        part: {
          id: SESSION_STATUS_PART_ID,
          kind: SessionPartKind._ClientStatus,
          state: ClientStatusSessionPartState.ErrorMessage,
          data: {
            message: sessionError?.message ?? "Something went wrong.",
            canRetry: sessionError?.canRetry ?? false,
            kind: sessionError?.type ?? MDSSessionErrorKind.SessionEventError,
          },
        },
        context: {
          eventKind: SessionEventKind.AgentActionV1,
          eventId: SESSION_STATUS_EVENT_ID,
          partId: SESSION_STATUS_PART_ID,
          itemId: SESSION_STATUS_ITEM_ID,
          lastArtifactId: null,
        },
      };
    }

    return {
      part: {
        id: SESSION_STATUS_PART_ID,
        kind: SessionPartKind._ClientStatus,
        state: ClientStatusSessionPartState.Placeholder,
        data: {},
      },
      context: {
        eventKind: SessionEventKind.AgentActionV1,
        eventId: SESSION_STATUS_EVENT_ID,
        partId: SESSION_STATUS_PART_ID,
        itemId: SESSION_STATUS_ITEM_ID,
        lastArtifactId: null,
      },
    };
  })();

  const sessionStatusItem: SessionItem = {
    id: SESSION_STATUS_ITEM_ID,
    kind: SessionItemKind.Standard,
    data: [sessionStatusItemData],
    context: { isExpanded: false },
  };

  return [...sessionItems, sessionStatusItem];
};
